import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  FiLink, FiCheck, FiX, FiRefreshCw, FiDatabase,
  FiServer, FiCpu, FiCloud, FiLock, FiUnlock, FiActivity,
  FiExternalLink, FiAlertCircle, FiCheckCircle, FiCopy
} from 'react-icons/fi';

const API_URL = process.env.REACT_APP_API_URL || 'https://cms.exploreandwin.com/api';

// Chain icons/logos as simple colored badges
const ChainBadge = ({ chain, size = 'md' }) => {
  const colors = {
    camino: 'bg-orange-500',
    algorand: 'bg-blue-500',
    polygon: 'bg-purple-500',
  };
  const sizeClasses = {
    sm: 'w-6 h-6 text-xs',
    md: 'w-8 h-8 text-sm',
    lg: 'w-10 h-10 text-base',
  };
  return (
    <div className={`${colors[chain] || 'bg-gray-500'} ${sizeClasses[size]} rounded-full flex items-center justify-center text-white font-bold`}>
      {chain?.charAt(0).toUpperCase()}
    </div>
  );
};

const BlockchainSettingsTab = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [config, setConfig] = useState(null);
  const [availableChains, setAvailableChains] = useState([]);
  const [chainConfigs, setChainConfigs] = useState({});
  const [stats, setStats] = useState(null);
  const [testResults, setTestResults] = useState({});
  const [copied, setCopied] = useState(null);

  // Form state
  const [activeChain, setActiveChain] = useState('camino');
  const [activeNetwork, setActiveNetwork] = useState('testnet');
  const [chainSettings, setChainSettings] = useState({
    camino: { network: 'testnet', contractAddress: '', rpcUrl: '', masterWalletAddress: '' },
    algorand: { network: 'testnet' },
    polygon: { network: 'testnet', contractAddress: '', rpcUrl: '', masterWalletAddress: '' },
  });
  const [ipfsSettings, setIpfsSettings] = useState({
    enabled: false,
    bucket: 'gamimap-nft',
    gateway: 'https://ipfs.filebase.io/ipfs/',
  });

  useEffect(() => {
    fetchConfig();
    fetchStats();
  }, []);

  const fetchConfig = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('cms_accessToken');
      const response = await axios.get(`${API_URL}/cms/blockchain-config`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const { config, availableChains, chainConfigs } = response.data;

      setConfig(config);
      setAvailableChains(availableChains);
      setChainConfigs(chainConfigs);

      // Initialize form state
      setActiveChain(config.activeChain || 'camino');
      setActiveNetwork(config.activeNetwork || 'testnet');

      setChainSettings({
        camino: {
          network: config.camino?.network || 'testnet',
          contractAddress: config.camino?.contractAddress || '',
          rpcUrl: config.camino?.rpcUrl || '',
          masterWalletAddress: config.camino?.masterWalletAddress || '',
        },
        algorand: {
          network: config.algorand?.network || 'testnet',
        },
        polygon: {
          network: config.polygon?.network || 'testnet',
          contractAddress: config.polygon?.contractAddress || '',
          rpcUrl: config.polygon?.rpcUrl || '',
          masterWalletAddress: config.polygon?.masterWalletAddress || '',
        },
      });

      setIpfsSettings({
        enabled: config.ipfs?.enabled || false,
        bucket: config.ipfs?.bucket || 'gamimap-nft',
        gateway: config.ipfs?.gateway || 'https://ipfs.filebase.io/ipfs/',
      });

    } catch (error) {
      console.error('Failed to fetch blockchain config:', error);
      alert('Failed to load blockchain configuration');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('cms_accessToken');
      const response = await axios.get(`${API_URL}/cms/blockchain-config/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const token = localStorage.getItem('cms_accessToken');

      await axios.put(
        `${API_URL}/cms/blockchain-config`,
        {
          activeChain,
          activeNetwork,
          camino: chainSettings.camino,
          algorand: chainSettings.algorand,
          polygon: chainSettings.polygon,
          ipfs: ipfsSettings,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      alert('Blockchain configuration saved successfully');
      fetchConfig();
    } catch (error) {
      console.error('Failed to save config:', error);
      alert('Failed to save blockchain configuration');
    } finally {
      setSaving(false);
    }
  };

  const handleTestConnection = async (chain) => {
    try {
      setTesting(chain);
      setTestResults(prev => ({ ...prev, [chain]: null }));

      const token = localStorage.getItem('cms_accessToken');
      const response = await axios.post(
        `${API_URL}/cms/blockchain-config/test-connection`,
        {
          chain,
          network: chainSettings[chain]?.network || 'testnet',
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setTestResults(prev => ({ ...prev, [chain]: response.data }));
    } catch (error) {
      setTestResults(prev => ({
        ...prev,
        [chain]: { success: false, error: error.response?.data?.error || error.message }
      }));
    } finally {
      setTesting(null);
    }
  };

  const handleSyncOwnership = async (exportToIPFS = false) => {
    if (!window.confirm(`This will sync NFT ownership from all configured chains${exportToIPFS ? ' and export to IPFS' : ''}. Continue?`)) {
      return;
    }

    try {
      setSyncing(true);
      const token = localStorage.getItem('cms_accessToken');

      const response = await axios.post(
        `${API_URL}/cms/blockchain-config/sync-ownership`,
        { exportToIPFS },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const { sync, export: exportResult } = response.data;
      let message = `Sync complete: ${sync.totalTransfers} transfers found, ${sync.totalUpdated} ownership records updated.`;
      if (exportResult?.success) {
        message += `\n\nExported to IPFS:\nCID: ${exportResult.cid}`;
      }
      alert(message);
      fetchStats();
    } catch (error) {
      console.error('Sync failed:', error);
      alert('Failed to sync ownership: ' + (error.response?.data?.error || error.message));
    } finally {
      setSyncing(false);
    }
  };

  const copyToClipboard = (text, key) => {
    navigator.clipboard.writeText(text);
    setCopied(key);
    setTimeout(() => setCopied(null), 2000);
  };

  const updateChainSetting = (chain, field, value) => {
    setChainSettings(prev => ({
      ...prev,
      [chain]: { ...prev[chain], [field]: value }
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <FiRefreshCw className="w-8 h-8 animate-spin text-primary-600" />
        <span className="ml-3 text-gray-600">Loading blockchain configuration...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <FiLink className="w-6 h-6 text-primary-600" />
          <h2 className="text-xl font-bold text-gray-900">Blockchain Configuration</h2>
        </div>
        <button
          onClick={handleSave}
          disabled={saving}
          className="btn btn-primary flex items-center gap-2"
        >
          {saving ? <FiRefreshCw className="w-4 h-4 animate-spin" /> : <FiCheck className="w-4 h-4" />}
          {saving ? 'Saving...' : 'Save Changes'}
        </button>
      </div>

      {/* Active Chain Selection */}
      <div className="bg-gradient-to-r from-primary-50 to-orange-50 rounded-xl p-6 border border-primary-200">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <FiCpu className="w-5 h-5 text-primary-600" />
          Active Blockchain
        </h3>
        <p className="text-sm text-gray-600 mb-4">
          Select the primary blockchain for NFT minting. Users will receive NFTs on this chain.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {availableChains.map((chain) => (
            <div
              key={chain.key}
              onClick={() => setActiveChain(chain.key)}
              className={`relative cursor-pointer rounded-xl p-4 border-2 transition-all ${
                activeChain === chain.key
                  ? 'border-primary-500 bg-white shadow-lg'
                  : 'border-gray-200 bg-white hover:border-gray-300'
              }`}
            >
              {activeChain === chain.key && (
                <div className="absolute top-2 right-2">
                  <FiCheckCircle className="w-5 h-5 text-primary-600" />
                </div>
              )}

              <div className="flex items-center gap-3 mb-3">
                <ChainBadge chain={chain.key} />
                <div>
                  <div className="font-semibold text-gray-900">{chain.name}</div>
                  <div className="text-xs text-gray-500">{chain.type.toUpperCase()}</div>
                </div>
              </div>

              <div className="space-y-1 text-sm">
                <div className="flex justify-between text-gray-600">
                  <span>Currency:</span>
                  <span className="font-medium">{chain.nativeCurrency}</span>
                </div>
                <div className="flex justify-between text-gray-600">
                  <span>Avg Cost:</span>
                  <span className="font-medium">{chain.avgCost || 'N/A'}</span>
                </div>
                <div className="flex justify-between text-gray-600">
                  <span>Speed:</span>
                  <span className="font-medium">{chain.avgSpeed || 'N/A'}</span>
                </div>
              </div>

              {chain.testnetContract && (
                <div className="mt-3 pt-3 border-t border-gray-100">
                  <div className="text-xs text-gray-500">Testnet Contract:</div>
                  <div className="text-xs font-mono text-gray-700 truncate">{chain.testnetContract}</div>
                </div>
              )}

              <div className="mt-3 flex flex-wrap gap-1">
                {chain.features?.slice(0, 3).map(f => (
                  <span key={f} className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded-full">
                    {f.replace(/_/g, ' ')}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Network Toggle */}
        <div className="mt-6 flex items-center gap-4">
          <span className="text-sm font-medium text-gray-700">Network:</span>
          <div className="flex bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => setActiveNetwork('testnet')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${
                activeNetwork === 'testnet'
                  ? 'bg-white text-primary-600 shadow'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Testnet
            </button>
            <button
              onClick={() => setActiveNetwork('mainnet')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${
                activeNetwork === 'mainnet'
                  ? 'bg-white text-primary-600 shadow'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Mainnet
            </button>
          </div>
          {activeNetwork === 'mainnet' && (
            <span className="text-xs text-orange-600 flex items-center gap-1">
              <FiAlertCircle className="w-4 h-4" />
              Production - Real transactions!
            </span>
          )}
        </div>
      </div>

      {/* Chain-Specific Settings */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <FiServer className="w-5 h-5 text-gray-600" />
            Chain Settings
          </h3>
        </div>

        <div className="p-6 space-y-6">
          {/* Camino Settings */}
          <div className="border border-gray-200 rounded-lg p-4">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <ChainBadge chain="camino" size="sm" />
                <span className="font-semibold text-gray-900">Camino (EVM)</span>
                {activeChain === 'camino' && (
                  <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-full">Active</span>
                )}
              </div>
              <button
                onClick={() => handleTestConnection('camino')}
                disabled={testing === 'camino'}
                className="btn btn-secondary text-sm flex items-center gap-2"
              >
                {testing === 'camino' ? (
                  <FiRefreshCw className="w-4 h-4 animate-spin" />
                ) : (
                  <FiActivity className="w-4 h-4" />
                )}
                Test Connection
              </button>
            </div>

            {testResults.camino && (
              <div className={`mb-4 p-3 rounded-lg ${testResults.camino.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
                <div className="flex items-center gap-2 text-sm">
                  {testResults.camino.success ? (
                    <>
                      <FiCheckCircle className="w-4 h-4 text-green-600" />
                      <span className="text-green-700">Connected - Block #{testResults.camino.blockNumber}</span>
                    </>
                  ) : (
                    <>
                      <FiX className="w-4 h-4 text-red-600" />
                      <span className="text-red-700">{testResults.camino.error}</span>
                    </>
                  )}
                </div>
                {testResults.camino.contract && (
                  <div className="mt-2 text-xs text-gray-600">
                    Contract: {testResults.camino.contract.name} - {testResults.camino.contract.totalSupply} NFTs minted
                  </div>
                )}
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Network</label>
                <select
                  value={chainSettings.camino.network}
                  onChange={(e) => updateChainSetting('camino', 'network', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                >
                  <option value="testnet">Columbus Testnet (Chain ID: 501)</option>
                  <option value="mainnet">Mainnet (Chain ID: 500)</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">NFT Contract Address</label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={chainSettings.camino.contractAddress}
                    onChange={(e) => updateChainSetting('camino', 'contractAddress', e.target.value)}
                    placeholder="0x..."
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 font-mono text-sm"
                  />
                  {chainSettings.camino.contractAddress && (
                    <button
                      onClick={() => copyToClipboard(chainSettings.camino.contractAddress, 'camino-contract')}
                      className="px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                      title="Copy address"
                    >
                      {copied === 'camino-contract' ? <FiCheck className="w-4 h-4 text-green-600" /> : <FiCopy className="w-4 h-4" />}
                    </button>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Master Wallet Address</label>
                <input
                  type="text"
                  value={chainSettings.camino.masterWalletAddress}
                  onChange={(e) => updateChainSetting('camino', 'masterWalletAddress', e.target.value)}
                  placeholder="0x..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 font-mono text-sm"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  RPC URL
                  <span className="text-gray-400 font-normal ml-1">(optional override)</span>
                </label>
                <input
                  type="text"
                  value={chainSettings.camino.rpcUrl}
                  onChange={(e) => updateChainSetting('camino', 'rpcUrl', e.target.value)}
                  placeholder={chainConfigs.camino?.networks?.testnet?.rpcUrl || 'Default RPC URL'}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 text-sm"
                />
              </div>
            </div>

            <div className="mt-3 flex items-center gap-2 text-xs text-gray-500">
              <FiLock className="w-3 h-3" />
              Private key managed via Secure Settings
            </div>
          </div>

          {/* Algorand Settings */}
          <div className="border border-gray-200 rounded-lg p-4">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <ChainBadge chain="algorand" size="sm" />
                <span className="font-semibold text-gray-900">Algorand</span>
                {activeChain === 'algorand' && (
                  <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-full">Active</span>
                )}
              </div>
              <button
                onClick={() => handleTestConnection('algorand')}
                disabled={testing === 'algorand'}
                className="btn btn-secondary text-sm flex items-center gap-2"
              >
                {testing === 'algorand' ? (
                  <FiRefreshCw className="w-4 h-4 animate-spin" />
                ) : (
                  <FiActivity className="w-4 h-4" />
                )}
                Test Connection
              </button>
            </div>

            {testResults.algorand && (
              <div className={`mb-4 p-3 rounded-lg ${testResults.algorand.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
                <div className="flex items-center gap-2 text-sm">
                  {testResults.algorand.success ? (
                    <>
                      <FiCheckCircle className="w-4 h-4 text-green-600" />
                      <span className="text-green-700">Connected - Round #{testResults.algorand.lastRound}</span>
                    </>
                  ) : (
                    <>
                      <FiX className="w-4 h-4 text-red-600" />
                      <span className="text-red-700">{testResults.algorand.error}</span>
                    </>
                  )}
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Network</label>
                <select
                  value={chainSettings.algorand.network}
                  onChange={(e) => updateChainSetting('algorand', 'network', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                >
                  <option value="testnet">TestNet</option>
                  <option value="mainnet">MainNet</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Wallet Status</label>
                <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 rounded-lg">
                  {config?.algorand?.hasWallet ? (
                    <>
                      <FiUnlock className="w-4 h-4 text-green-600" />
                      <span className="text-sm text-green-700">Wallet configured</span>
                    </>
                  ) : (
                    <>
                      <FiLock className="w-4 h-4 text-gray-400" />
                      <span className="text-sm text-gray-500">No wallet configured</span>
                    </>
                  )}
                </div>
              </div>
            </div>

            {config?.algorand?.walletAddress && (
              <div className="mt-3 text-xs text-gray-500">
                Address: <span className="font-mono">{config.algorand.walletAddress}</span>
              </div>
            )}
          </div>

          {/* Polygon Settings */}
          <div className="border border-gray-200 rounded-lg p-4">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <ChainBadge chain="polygon" size="sm" />
                <span className="font-semibold text-gray-900">Polygon (EVM)</span>
                {activeChain === 'polygon' && (
                  <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-full">Active</span>
                )}
                <span className="px-2 py-0.5 bg-yellow-100 text-yellow-700 text-xs rounded-full">Coming Soon</span>
              </div>
              <button
                onClick={() => handleTestConnection('polygon')}
                disabled={testing === 'polygon'}
                className="btn btn-secondary text-sm flex items-center gap-2"
              >
                {testing === 'polygon' ? (
                  <FiRefreshCw className="w-4 h-4 animate-spin" />
                ) : (
                  <FiActivity className="w-4 h-4" />
                )}
                Test Connection
              </button>
            </div>

            {testResults.polygon && (
              <div className={`mb-4 p-3 rounded-lg ${testResults.polygon.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
                <div className="flex items-center gap-2 text-sm">
                  {testResults.polygon.success ? (
                    <>
                      <FiCheckCircle className="w-4 h-4 text-green-600" />
                      <span className="text-green-700">Connected - Block #{testResults.polygon.blockNumber}</span>
                    </>
                  ) : (
                    <>
                      <FiX className="w-4 h-4 text-red-600" />
                      <span className="text-red-700">{testResults.polygon.error}</span>
                    </>
                  )}
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Network</label>
                <select
                  value={chainSettings.polygon.network}
                  onChange={(e) => updateChainSetting('polygon', 'network', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                >
                  <option value="testnet">Amoy Testnet (Chain ID: 80002)</option>
                  <option value="mainnet">Mainnet (Chain ID: 137)</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">NFT Contract Address</label>
                <input
                  type="text"
                  value={chainSettings.polygon.contractAddress}
                  onChange={(e) => updateChainSetting('polygon', 'contractAddress', e.target.value)}
                  placeholder="0x... (not deployed)"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 font-mono text-sm"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* IPFS Settings */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <FiCloud className="w-5 h-5 text-gray-600" />
            IPFS Storage (Filebase)
          </h3>
        </div>

        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={ipfsSettings.enabled}
                  onChange={(e) => setIpfsSettings(prev => ({ ...prev, enabled: e.target.checked }))}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
              </label>
              <span className="font-medium text-gray-900">Enable IPFS Storage</span>
            </div>
            {config?.ipfs?.hasCredentials ? (
              <span className="flex items-center gap-1 text-sm text-green-600">
                <FiCheckCircle className="w-4 h-4" />
                Credentials configured
              </span>
            ) : (
              <span className="flex items-center gap-1 text-sm text-gray-500">
                <FiAlertCircle className="w-4 h-4" />
                No credentials (configure in Secure Settings)
              </span>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Bucket Name</label>
              <input
                type="text"
                value={ipfsSettings.bucket}
                onChange={(e) => setIpfsSettings(prev => ({ ...prev, bucket: e.target.value }))}
                placeholder="gamimap-nft"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">IPFS Gateway</label>
              <input
                type="text"
                value={ipfsSettings.gateway}
                onChange={(e) => setIpfsSettings(prev => ({ ...prev, gateway: e.target.value }))}
                placeholder="https://ipfs.filebase.io/ipfs/"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              />
            </div>
          </div>

          <p className="mt-3 text-xs text-gray-500">
            Filebase provides S3-compatible IPFS pinning. NFT images and metadata are stored permanently on IPFS.
            Credentials are managed via Secure Settings.
          </p>
        </div>
      </div>

      {/* Ownership Registry & Stats */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="bg-gray-50 px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <FiDatabase className="w-5 h-5 text-gray-600" />
            NFT Ownership Registry
          </h3>
          <div className="flex gap-2">
            <button
              onClick={() => handleSyncOwnership(false)}
              disabled={syncing}
              className="btn btn-secondary text-sm flex items-center gap-2"
            >
              {syncing ? <FiRefreshCw className="w-4 h-4 animate-spin" /> : <FiRefreshCw className="w-4 h-4" />}
              Sync Now
            </button>
            <button
              onClick={() => handleSyncOwnership(true)}
              disabled={syncing}
              className="btn btn-primary text-sm flex items-center gap-2"
            >
              {syncing ? <FiRefreshCw className="w-4 h-4 animate-spin" /> : <FiCloud className="w-4 h-4" />}
              Sync & Export
            </button>
          </div>
        </div>

        <div className="p-6">
          <p className="text-sm text-gray-600 mb-4">
            The ownership registry tracks all NFT owners across chains for disaster recovery.
            A cron job syncs ownership every 3 days and exports to IPFS.
          </p>

          {/* Stats Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            {stats?.mintStats?.map((stat) => (
              <div key={stat.chain} className="bg-gray-50 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <ChainBadge chain={stat.chain} size="sm" />
                  <span className="font-medium text-gray-900 capitalize">{stat.chain}</span>
                </div>
                <div className="text-2xl font-bold text-gray-900">{stat.total_minted}</div>
                <div className="text-xs text-gray-500">{stat.unique_users} unique users</div>
              </div>
            ))}
            {(!stats?.mintStats || stats.mintStats.length === 0) && (
              <div className="col-span-4 text-center text-gray-500 py-4">
                No NFTs minted yet
              </div>
            )}
          </div>

          {/* Last Export Info */}
          {stats?.registryStats?.lastExport && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm font-medium text-blue-900">Last IPFS Export</div>
                  <div className="text-xs text-blue-700">
                    {new Date(stats.registryStats.lastExport.exported_at).toLocaleString()}
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-sm text-blue-700">
                    {stats.registryStats.lastExport.total_tokens} tokens, {stats.registryStats.lastExport.total_owners} owners
                  </div>
                  <a
                    href={`https://ipfs.filebase.io/ipfs/${stats.registryStats.lastExport.cid}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-blue-600 hover:underline flex items-center gap-1 justify-end"
                  >
                    View on IPFS <FiExternalLink className="w-3 h-3" />
                  </a>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BlockchainSettingsTab;
